import {
    InputAdornment,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { useNavigate, useParams } from "react-router-dom";
import Image1 from '../../images/questions/image1.png';
import SnackbarAlert from "../../components/SnackBar";
import { buildQuestionFormData } from "../../helper/QuestionFormData";
import Fetch from "../../services/Fetch";
import { useConstants } from "../../hooks/UseConstants";
import PageContent from "../../components/PageContent";
import PageHero from "../../components/motion/PageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import QuizIcon from "@mui/icons-material/Quiz";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentIcon from "@mui/icons-material/Assignment";
import GradeOutlinedIcon from "@mui/icons-material/GradeOutlined";

function inferTrueFalseAnswer(question) {
     const correct = (question?.options || []).find((item) => item.is_correct);
     if (!correct) return "true";

     const en = String(correct.text_en || "").toLowerCase();
     const ar = String(correct.text_ar || "");
     if (en.includes("false") || ar.includes("خط")) return "false";
     return "true";
}

function UpdateQuestion() {
     const { host, language } = useConstants();
     const isAr = language === "ar";
     const theme = useTheme();
     const { wait } = useContext(AuthContext);
     const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
     const { sendWait, setSendWait, setGetWait, getWait } = useWaits();
     const [exam, setExam] = useState(null);
     const [question, setQuestion] = useState(null);
     const [textEn, setTextEn] = useState("");
     const [textAr, setTextAr] = useState("");
     const [questionType, setQuestionType] = useState("");
     const [correctAnswer, setCorrectAnswer] = useState("true");
     const [fullMark, setFullMark] = useState("1");
     const param = useParams();
     const navigate = useNavigate();

     const questionsPath = `/courses/${param.course_id}/exams/${param.exam_id}/questions`;

     const examName = useMemo(() => {
          if (!exam) return "";
          return isAr ? exam.name_ar : exam.name_en;
     }, [exam, isAr]);

     const getQuestion = async () => {
          setGetWait(true);
          const result = await Fetch(`${host}/courses/${param.course_id}/exams/${param.exam_id}/show`, 'GET', null);

          if (result.status === 200) {
               const examData = result.data.data;
               const selectedQuestion = (examData.questions || []).find((item) => String(item.id) === String(param.question_id));
               setExam(examData);
               setQuestion(selectedQuestion || null);

               if (selectedQuestion) {
                    setTextEn(selectedQuestion.text_en || "");
                    setTextAr(selectedQuestion.text_ar || "");
                    setQuestionType(selectedQuestion.type || "");
                    setFullMark(String(selectedQuestion.full_mark ?? "1"));
                    if (selectedQuestion.type === "true_false") {
                         setCorrectAnswer(inferTrueFalseAnswer(selectedQuestion));
                    }
               }
          }

          setGetWait(false);
     };

     useEffect(() => {
          getQuestion();
     }, [param.course_id, param.exam_id, param.question_id]);

     const updateQuestion = async () => {
          if (!textEn.trim() || !textAr.trim()) {
               setSnackBar("error", isAr ? "أدخل نص السؤال بالعربية والإنجليزية" : "Enter question text in Arabic and English");
               return;
          }
          if (!questionType) {
               setSnackBar("error", isAr ? "اختر نوع السؤال" : "Select question type");
               return;
          }
          if (!fullMark || Number(fullMark) <= 0) {
               setSnackBar("error", isAr ? "أدخل درجة السؤال" : "Enter question mark");
               return;
          }
          if (questionType === "true_false" && !correctAnswer) {
               setSnackBar("error", isAr ? "حدد الإجابة الصحيحة" : "Select the correct answer");
               return;
          }

          setSendWait(true);
          const formData = buildQuestionFormData({
               textEn: textEn.trim(),
               textAr: textAr.trim(),
               questionType,
               fullMark,
               correctAnswer: questionType === "true_false" ? correctAnswer === "true" : undefined,
          });

          const result = await Fetch(`${host}/teacher/courses/exams/questions/${param.question_id}/update`, 'POST', formData);

          if (result.status === 200) {
               setSnackBar('success', isAr ? 'تم تحديث السؤال بنجاح' : 'Question updated successfully');
               navigate(questionsPath);
          } else if (result.status === 422) {
               setSnackBar('error', result.data.errors?.[0] || result.data.message);
          } else {
               setSnackBar('error', isAr ? 'تعذر تحديث السؤال' : 'Failed to update question');
          }

          setSendWait(false);
     };

     const fieldSx = { width: "100%" };
     const questionTypes = [
          { value: "multiple_options", labelEn: "Multiple choice", labelAr: "اختيار من متعدد" },
          { value: "true_false", labelEn: "True or false", labelAr: "صح أو خطأ" },
     ];

     return (
          <>
               <PageContent wait={wait} loading={getWait}>
                    <PageHero
                         title={isAr ? "تعديل السؤال" : "Update question"}
                         subtitle={isAr ? "عدّل نص السؤال ونوعه ودرجته" : "Edit question text, type, and mark"}
                         icon={<QuizIcon />}
                         actions={
                              <>
                                   <Button
                                        variant="contained"
                                        startIcon={sendWait ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
                                        onClick={updateQuestion}
                                        disabled={sendWait}
                                        sx={{ bgcolor: "rgba(255,255,255,0.95)", color: "#0D358C", fontWeight: 700, "&:hover": { bgcolor: "#fff" } }}
                                   >
                                        {isAr ? "حفظ التعديلات" : "Save changes"}
                                   </Button>
                                   <Button
                                        variant="outlined"
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => navigate(questionsPath)}
                                        sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)", "&:hover": { borderColor: "white" } }}
                                   >
                                        {isAr ? "عودة للأسئلة" : "Back to questions"}
                                   </Button>
                              </>
                         }
                    />

                    {exam ? (
                         <ScrollReveal>
                              <Box
                                   sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        mb: 3,
                                        p: 2,
                                        borderRadius: 3,
                                        bgcolor: theme.palette.background.paper,
                                        border: "1px solid",
                                        borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                   }}
                              >
                                   <Box
                                        sx={{
                                             width: 56,
                                             height: 56,
                                             borderRadius: 2,
                                             bgcolor: "#eff6ff",
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                             flexShrink: 0,
                                        }}
                                   >
                                        <AssignmentIcon sx={{ color: "#0D358C", fontSize: 32 }} />
                                   </Box>
                                   <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                             {isAr ? "الاختبار المختار" : "Selected exam"}
                                        </Typography>
                                        <Typography variant="h6" fontWeight={800} sx={{ color: "#0D358C" }} noWrap>
                                             {examName}
                                        </Typography>
                                        <Chip size="small" label={`ID #${exam.id}`} sx={{ mt: 0.5 }} />
                                   </Box>
                              </Box>
                         </ScrollReveal>
                    ) : null}

                    {question ? (
                         <Box
                              sx={{
                                   display: "flex",
                                   flexDirection: { xs: "column", lg: "row" },
                                   direction: "ltr",
                                   gap: 3,
                                   alignItems: "stretch",
                              }}
                         >
                              <ScrollReveal delay={50} className="w-full lg:w-[38%]">
                                   <Box
                                        sx={{
                                             height: "100%",
                                             minHeight: { lg: 400 },
                                             display: "flex",
                                             flexDirection: "column",
                                             alignItems: "center",
                                             justifyContent: "center",
                                             gap: 2,
                                             p: 4,
                                             borderRadius: 4,
                                             border: "1px solid",
                                             borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                             background: theme.palette.mode === "dark"
                                                  ? "linear-gradient(160deg, #1a2744 0%, #121212 100%)"
                                                  : "linear-gradient(160deg, #e8f0fa 0%, #f5f8fc 100%)",
                                        }}
                                   >
                                        <Box
                                             component="img"
                                             src={Image1}
                                             alt=""
                                             sx={{ width: "100%", maxWidth: 280, height: "auto", objectFit: "contain" }}
                                        />
                                        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", maxWidth: 280, lineHeight: 1.7 }}>
                                             {questionType === "true_false"
                                                  ? (isAr
                                                       ? "في أسئلة صح وخطأ يتم تحديث الخيار الصحيح مباشرة بدون إدارة يدوية للخيارات."
                                                       : "For true/false questions, the correct answer is updated directly without manual option management.")
                                                  : (isAr
                                                       ? "يمكنك تعديل نص السؤال ودرجته، أما خيارات الإجابة فتدار من قائمة الخيارات."
                                                       : "You can edit the question text and mark; answer options remain managed from the options list.")}
                                        </Typography>
                                   </Box>
                              </ScrollReveal>

                              <Box sx={{ flex: 1, minWidth: 0, direction: isAr ? "rtl" : "ltr" }}>
                                   <DashboardPanelCard
                                        title={isAr ? "بيانات السؤال" : "Question details"}
                                        subtitle={isAr ? "حدّث السؤال وسيحفظ فوراً في الاختبار" : "Update the question and save it back to the exam"}
                                   >
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                             <TextField
                                                  sx={fieldSx}
                                                  value={textEn}
                                                  onChange={(e) => setTextEn(e.target.value)}
                                                  label={isAr ? "نص السؤال بالإنجليزية" : "Question text (English)"}
                                                  variant="outlined"
                                                  size="small"
                                                  multiline
                                                  minRows={3}
                                                  required
                                             />
                                             <TextField
                                                  sx={fieldSx}
                                                  value={textAr}
                                                  onChange={(e) => setTextAr(e.target.value)}
                                                  label={isAr ? "نص السؤال بالعربية" : "Question text (Arabic)"}
                                                  variant="outlined"
                                                  size="small"
                                                  multiline
                                                  minRows={3}
                                                  required
                                             />

                                             <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                                                  <FormControl fullWidth size="small" required>
                                                       <InputLabel id="question-type-update-label">
                                                            {isAr ? "نوع السؤال" : "Question type"}
                                                       </InputLabel>
                                                       <Select
                                                            labelId="question-type-update-label"
                                                            value={questionType}
                                                            label={isAr ? "نوع السؤال" : "Question type"}
                                                            onChange={(e) => setQuestionType(e.target.value)}
                                                       >
                                                            {questionTypes.map((item) => (
                                                                 <MenuItem key={item.value} value={item.value}>
                                                                      {isAr ? item.labelAr : item.labelEn}
                                                                 </MenuItem>
                                                            ))}
                                                       </Select>
                                                  </FormControl>

                                                  <TextField
                                                       sx={fieldSx}
                                                       type="number"
                                                       value={fullMark}
                                                       onChange={(e) => setFullMark(e.target.value)}
                                                       label={isAr ? "درجة السؤال" : "Question mark"}
                                                       variant="outlined"
                                                       size="small"
                                                       required
                                                       inputProps={{ min: 0.5, step: 0.5 }}
                                                       InputProps={{
                                                            startAdornment: (
                                                                 <InputAdornment position="start">
                                                                      <GradeOutlinedIcon fontSize="small" color="action" />
                                                                 </InputAdornment>
                                                            ),
                                                       }}
                                                  />
                                             </Box>

                                             {questionType === "true_false" ? (
                                                  <FormControl component="fieldset" sx={{ mt: 0.5 }}>
                                                       <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: "#0D358C" }}>
                                                            {isAr ? "الإجابة الصحيحة" : "Correct answer"}
                                                       </Typography>
                                                       <RadioGroup
                                                            row
                                                            value={correctAnswer}
                                                            onChange={(e) => setCorrectAnswer(e.target.value)}
                                                       >
                                                            <FormControlLabel value="true" control={<Radio color="primary" />} label={isAr ? "صح" : "True"} />
                                                            <FormControlLabel value="false" control={<Radio color="primary" />} label={isAr ? "خطأ" : "False"} />
                                                       </RadioGroup>
                                                  </FormControl>
                                             ) : null}

                                             <Box
                                                  sx={{
                                                       display: "flex",
                                                       flexWrap: "wrap",
                                                       gap: 1.5,
                                                       justifyContent: "flex-end",
                                                       mt: 2,
                                                       pt: 2,
                                                       borderTop: "1px solid",
                                                       borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                                  }}
                                             >
                                                  <Button variant="outlined" onClick={() => navigate(questionsPath)} disabled={sendWait}>
                                                       {isAr ? "إلغاء" : "Cancel"}
                                                  </Button>
                                                  <Button
                                                       variant="contained"
                                                       size="large"
                                                       onClick={updateQuestion}
                                                       disabled={sendWait}
                                                       startIcon={sendWait ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
                                                       sx={{
                                                            minWidth: 220,
                                                            px: 3,
                                                            bgcolor: "#0D358C",
                                                            fontWeight: 700,
                                                            boxShadow: "0 4px 14px rgba(13,53,140,0.35)",
                                                            "&:hover": { bgcolor: "#1d4ed8" },
                                                       }}
                                                  >
                                                       {sendWait ? (isAr ? "جاري الحفظ..." : "Saving...") : (isAr ? "حفظ التعديلات" : "Save changes")}
                                                  </Button>
                                             </Box>
                                        </Box>
                                   </DashboardPanelCard>
                              </Box>
                         </Box>
                    ) : (
                         !getWait && (
                              <Typography sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
                                   {isAr ? "تعذر تحميل السؤال" : "Could not load question"}
                              </Typography>
                         )
                    )}
               </PageContent>
               <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
          </>
     );
}

export default UpdateQuestion;